/**
 * @module NotificationQueries
 * @package @geenius/notifications-convex
 * @description Defines publishable Convex query functions for notification
 * inbox data, unread counts, and user preference lookups.
 */

import { v } from 'convex/values'
import { query } from './_generated/server.js'

const notificationStatusValidator = v.union(
  v.literal('unread'),
  v.literal('read'),
  v.literal('dismissed'),
)

const notificationTypeValidator = v.union(
  v.literal('info'),
  v.literal('success'),
  v.literal('warning'),
  v.literal('error'),
  v.literal('mention'),
  v.literal('invite'),
  v.literal('payment'),
  v.literal('system'),
)

/**
 * Lists notifications for a user, with optional status and type filtering.
 *
 * @param args.userId Target user identifier.
 * @param args.status Optional status filter.
 * @param args.type Optional type filter.
 * @param args.limit Optional result limit.
 * @returns Notifications ordered from newest to oldest.
 */
export const listNotifications = query({
  args: {
    userId: v.string(),
    status: v.optional(notificationStatusValidator),
    type: v.optional(notificationTypeValidator),
    limit: v.optional(v.number()),
  },
  handler: async (ctx, args) => {
    const limit = args.limit ?? 50
    const indexedNotifications = args.status
      ? await ctx.db
          .query('notifications')
          .withIndex('by_userId_status', (q) => q.eq('userId', args.userId))
          .order('desc')
          .take(limit * 2)
      : await ctx.db
          .query('notifications')
          .withIndex('by_userId_createdAt', (q) => q.eq('userId', args.userId))
          .order('desc')
          .take(limit * 2)

    const statusFiltered = args.status
      ? indexedNotifications.filter(
          (notification) => notification.status === args.status,
        )
      : indexedNotifications
    const typeFiltered = args.type
      ? statusFiltered.filter((notification) => notification.type === args.type)
      : statusFiltered

    return typeFiltered.slice(0, limit)
  },
})

/**
 * Counts unread notifications for a single user.
 *
 * @param args.userId Target user identifier.
 * @returns Total unread notification count.
 */
export const getUnreadCount = query({
  args: { userId: v.string() },
  handler: async (ctx, args) => {
    const notifications = await ctx.db
      .query('notifications')
      .withIndex('by_userId_status', (q) => q.eq('userId', args.userId))
      .collect()
    const unread = notifications.filter(
      (notification) => notification.status === 'unread',
    )
    return unread.length
  },
})

/**
 * Looks up persisted notification preferences for a user.
 *
 * @param args.userId Target user identifier.
 * @returns The stored preference document, or `null` when absent.
 */
export const getNotificationPreferences = query({
  args: { userId: v.string() },
  handler: async (ctx, args) => {
    return ctx.db.query('notification_preferences')
      .withIndex('by_userId', (q) => q.eq('userId', args.userId))
      .first()
  },
})

/**
 * Lists the most recent notifications for activity summaries.
 *
 * @param args.userId Target user identifier.
 * @param args.limit Optional result limit.
 * @returns Recent notification records ordered from newest to oldest.
 */
export const listRecentActivity = query({
  args: { userId: v.string(), limit: v.optional(v.number()) },
  handler: async (ctx, args) => {
    return ctx.db.query('notifications')
      .withIndex('by_userId_createdAt', (q) => q.eq('userId', args.userId))
      .order('desc')
      .take(args.limit ?? 10)
  },
})
